import React from 'react';
import { useLandingPageData } from '../../context/LandingPageContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../i18n/translations';
import { Shield, Award, Heart, Clock, Euro, Users, MapPin, Eye, Target, Sparkles, Scissors, HeartHandshake, MessageSquare, Star } from 'lucide-react';

const iconMap = { Shield, Award, Heart, Clock, Euro, Users, MapPin, Eye, Target, Sparkles, Scissors, HeartHandshake, MessageSquare, Star };

const Expertise = () => {
  const { expertise } = useLandingPageData();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  
  // Comprehensive translations for all expertise items across all variants
  const expertiseTranslations = {
    // Main page expertise
    'Modernste Technologie': { en: 'Latest Technology', de: 'Modernste Technologie' },
    'Erfahrene Spezialisten': { en: 'Experienced Specialists', de: 'Erfahrene Spezialisten' },
    'Natürliche Ergebnisse': { en: 'Natural Results', de: 'Natürliche Ergebnisse' },
    'Schnelle Erholung': { en: 'Quick Recovery', de: 'Schnelle Erholung' },
    'Transparente Preise': { en: 'Transparent Prices', de: 'Transparente Preise' },
    'Zentrale Lage': { en: 'Central Location', de: 'Zentrale Lage' },
    
    // Eyebrow page expertise
    'Natürliche Form': { en: 'Natural Shape', de: 'Natürliche Form' },
    'Präzisionsarbeit': { en: 'Precision Work', de: 'Präzisionsarbeit' },
    'Dauerhafte Lösung': { en: 'Permanent Solution', de: 'Dauerhafte Lösung' },
    'Für alle Ursachen': { en: 'For All Causes', de: 'Für alle Ursachen' },
    'Sichere Methode': { en: 'Safe Method', de: 'Sichere Methode' },
    'Spezialisiertes Team': { en: 'Specialized Team', de: 'Spezialisiertes Team' },
    
    // Beard page expertise
    'Voller Bart': { en: 'Full Beard', de: 'Voller Bart' },
    'Natürliche Dichte': { en: 'Natural Density', de: 'Natürliche Dichte' },
    'Individuelle Kontur': { en: 'Individual Contour', de: 'Individuelle Kontur' },
    'Narbenabdeckung': { en: 'Scar Coverage', de: 'Narbenabdeckung' },
    'Dauerhafte Ergebnisse': { en: 'Permanent Results', de: 'Dauerhafte Ergebnisse' },
    'Bartlücken schließen': { en: 'Close Beard Gaps', de: 'Bartlücken schließen' },
    
    // Beard page expertise (from mock-bart.js)
    'Erfahrenes Expertenteam': { en: 'Experienced Expert Team', de: 'Erfahrenes Expertenteam' },
    'Modernste Verfahren': { en: 'Latest Procedures', de: 'Modernste Verfahren' },
    'Ganzheitliches Konzept': { en: 'Holistic Concept', de: 'Ganzheitliches Konzept' },
    'Individuelle Beratung': { en: 'Individual Consultation', de: 'Individuelle Beratung' },
    'Umfassende Nachsorge': { en: 'Comprehensive Aftercare', de: 'Umfassende Nachsorge' },
    'Natürliche Bartlinien': { en: 'Natural Beard Lines', de: 'Natürliche Bartlinien' },
    'Langzeitnachsorge': { en: 'Long-term Aftercare', de: 'Langzeitnachsorge' },
    'Keine Auslandsreise nötig': { en: 'No Trip Abroad Needed', de: 'Keine Auslandsreise nötig' },
    
    // DHI page expertise
    '95%+ Anwachsrate': { en: '95%+ Growth Rate', de: '95%+ Anwachsrate' },
    'Rundum-Betreuung': { en: 'Complete Care', de: 'Rundum-Betreuung' },
  };

  const descriptionTranslations = {
    // Main page descriptions
    'Wir setzen auf die neuesten Technologien in der Haartransplantation für beste Ergebnisse.': {
      en: 'We rely on the latest technologies in hair transplantation for the best results.'
    },
    'Unser Team besteht aus erfahrenen Spezialisten mit über 15 Jahren Erfahrung.': {
      en: 'Our team consists of experienced specialists with over 15 years of experience.'
    },
    'Unsere Priorität: Natürlich aussehende Ergebnisse, die Ihr Erscheinungsbild verbessern.': {
      en: 'Our priority: Natural-looking results that enhance your appearance.'
    },
    'Dank moderner Techniken ist die Erholungszeit minimal und Sie sind schnell wieder fit.': {
      en: 'Thanks to modern techniques, recovery time is minimal and you\'ll be back on your feet quickly.'
    },
    'Bei uns gibt es keine versteckten Kosten. Sie erhalten einen transparenten Kostenplan.': {
      en: 'With us, there are no hidden costs. You receive a transparent cost plan.'
    },
    'Unsere Klinik in Mönchengladbach ist ideal erreichbar aus Köln, Düsseldorf und der Region.': {
      en: 'Our clinic in Mönchengladbach is ideally accessible from Cologne, Düsseldorf and the region.'
    },
    
    // Eyebrow page descriptions
    'Wir gestalten Ihre Augenbrauen passend zu Ihrer Gesichtsform für einen harmonischen, natürlichen Look.': {
      en: 'We design your eyebrows to match your face shape for a harmonious, natural look.'
    },
    'Jedes einzelne Haar wird im optimalen Winkel implantiert, um natürliches Wachstum zu imitieren.': {
      en: 'Each individual hair is implanted at the optimal angle to imitate natural growth.'
    },
    'Anders als Microblading oder Permanent Make-up wachsen transplantierte Augenbrauen dauerhaft.': {
      en: 'Unlike microblading or permanent makeup, transplanted eyebrows grow permanently.'
    },
    'Ob Überzupfen, genetisch dünne Brauen, Narben oder Alopecia - wir finden die passende Lösung.': {
      en: 'Whether over-plucking, genetically thin brows, scars or alopecia - we find the right solution.'
    },
    'Die Transplantation erfolgt unter lokaler Betäubung und ist minimalinvasiv.': {
      en: 'The transplant is performed under local anesthesia and is minimally invasive.'
    },
    'Unsere Ärzte haben jahrelange Erfahrung speziell in der Augenbrauentransplantation.': {
      en: 'Our doctors have years of experience specifically in eyebrow transplantation.'
    },
    
    // Beard page descriptions
    'Egal ob Sie einen Vollbart, Dreitagebart oder nur einzelne Bereiche verdichten möchten - wir haben die passende Lösung.': {
      en: 'Whether you want a full beard, stubble or just want to densify individual areas - we have the right solution.'
    },
    'Durch präzise Implantation erreichen wir eine natürliche Haardichte, die zu Ihrem Gesicht passt.': {
      en: 'Through precise implantation, we achieve natural hair density that suits your face.'
    },
    'Wir gestalten Ihre Bartkontur nach Ihren Wünschen für einen maskulinen, gepflegten Look.': {
      en: 'We design your beard contour according to your wishes for a masculine, well-groomed look.'
    },
    'Narben im Bartbereich können gezielt mit Haartransplantation abgedeckt werden.': {
      en: 'Scars in the beard area can be specifically covered with hair transplantation.'
    },
    'Die transplantierten Haare wachsen dauerhaft und können wie Ihr eigener Bart gestylt werden.': {
      en: 'The transplanted hair grows permanently and can be styled like your own beard.'
    },
    'Lücken im Bart werden präzise mit eigenen Haarfollikeln aufgefüllt.': {
      en: 'Gaps in the beard are precisely filled with your own hair follicles.'
    },
    // Beard page descriptions (from mock-bart.js)
    'Unser Team besteht aus hochqualifizierten Spezialisten mit jahrelanger Erfahrung in der Barthaartransplantation, die regelmäßig an Fortbildungen teilnehmen.': {
      en: 'Our team consists of highly qualified specialists with years of experience in beard hair transplantation who regularly participate in advanced training.'
    },
    'Wir setzen auf innovative Techniken wie Saphir-FUE und DHI, die präzisere Ergebnisse, schnellere Heilung und natürlicheres Aussehen ermöglichen.': {
      en: 'We rely on innovative techniques such as Sapphire FUE and DHI, which enable more precise results, faster healing and a more natural appearance.'
    },
    'Unser 360°-Ansatz umfasst nicht nur die Transplantation, sondern auch umfassende Analyse, Ernährungsberatung und ergänzende Behandlungen.': {
      en: 'Our 360° approach includes not only the transplant, but also comprehensive analysis, nutritional advice and complementary treatments.'
    },
    'Jeder Patient erhält einen maßgeschneiderten Behandlungsplan, der auf seine spezifischen Bedürfnisse, Bartstruktur und Wünsche abgestimmt ist.': {
      en: 'Each patient receives a customized treatment plan tailored to their specific needs, beard structure and wishes.'
    },
    'Von der ersten Beratung bis zur vollständigen Genesung begleiten wir Sie mit regelmäßigen Kontrollen und persönlicher Betreuung.': {
      en: 'From the initial consultation to full recovery, we accompany you with regular check-ups and personal care.'
    },
    'Dank modernster Techniken erzielen wir natürlich wirkende Bartlinien und Konturen, die perfekt zu Ihrer Gesichtsform passen.': {
      en: 'Thanks to state-of-the-art techniques, we achieve natural-looking beard lines and contours that perfectly match your face shape.'
    },
    'Wir begleiten Sie auch nach dem Eingriff mit regelmäßigen Kontrollterminen und unterstützenden Behandlungen für langfristig beste Resultate.': {
      en: 'We accompany you even after the procedure with regular check-ups and supportive treatments for the best long-term results.'
    },
    'Höchste medizinische Standards in Deutschland ohne Sprachbarrieren. Zentrale Lage in NRW, ideal erreichbar aus Köln und Düsseldorf.': {
      en: 'Highest medical standards in Germany without language barriers. Central location in NRW, easily accessible from Cologne and Düsseldorf.'
    },
    // DHI page descriptions
    'Wir verwenden die neueste Saphir DHI Hybridtechnik mit Choi-Pen für maximale Präzision und natürliche Ergebnisse.': {
      en: 'We use the latest Sapphire DHI hybrid technique with Choi Pen for maximum precision and natural results.'
    },
    'Unser Team hat über 5.000 erfolgreiche Behandlungen durchgeführt. Jede DHI-Transplantation wird von spezialisierten Chirurgen geleitet.': {
      en: 'Our team has performed over 5,000 successful treatments. Every DHI transplant is led by specialized surgeons.'
    },
    'Durch die direkte Implantation mit dem Choi-Pen sind die Grafts kürzer außerhalb des Körpers — das erhöht die Überlebensrate.': {
      en: 'Through direct implantation with the Choi Pen, grafts spend less time outside the body — increasing the survival rate.'
    },
    'Von der kostenlosen Erstberatung über die Behandlung bis zur 12-monatigen Nachsorge — wir begleiten Sie persönlich.': {
      en: 'From the free initial consultation to the treatment and 12-month aftercare — we accompany you personally.'
    },
  };

  const getTranslatedTitle = (title) => {
    if (language === 'de') return title;
    return expertiseTranslations[title]?.en || title;
  };

  const getTranslatedDescription = (desc) => {
    if (language === 'de') return desc;
    return descriptionTranslations[desc]?.en || desc;
  };
  
  return (
    <section id="expertise" className={`py-24 relative overflow-hidden transition-colors duration-300 ${
      isDark ? 'bg-[#0a0a0a]' : 'bg-white'
    }`}>
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className={`absolute bottom-0 left-1/4 w-[500px] h-[500px] rounded-full blur-[80px] ${
          isDark ? 'bg-[#5A8BA6]/10' : 'bg-[#5A8BA6]/5'
        }`} />
        <div className={`absolute top-1/4 right-0 w-[400px] h-[400px] rounded-full blur-[60px] ${
          isDark ? 'bg-[#7BA7C2]/5' : 'bg-[#7BA7C2]/5'
        }`} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium mb-4 ${
            isDark ? 'bg-[#7BA7C2]/10 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 text-[#5A8BA6]'
          }`}>
            {language === 'de' ? 'Warum Dion Hair Clinic' : 'Why Dion Hair Clinic'}
          </span>
          <h2 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {language === 'de' ? 'Unsere ' : 'Our '}
            <span className="bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] bg-clip-text text-transparent">
              {language === 'de' ? 'Expertise' : 'Expertise'}
            </span>
          </h2>
          <p className={`max-w-2xl mx-auto text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {t('expertise.sectionDescription')}
          </p>
        </div>

        {/* Expertise Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {expertise.map((item, index) => {
            const IconComponent = iconMap[item.icon];
            const translatedTitle = getTranslatedTitle(item.title);
            const translatedDesc = getTranslatedDescription(item.description);
            
            return (
              <div
                key={item.id}
                className={`group relative p-8 rounded-2xl border transition-all duration-500 card-lift ${
                  isDark 
                    ? 'bg-gradient-to-br from-white/[0.03] to-transparent border-white/[0.05] hover:border-[#7BA7C2]/30' 
                    : 'bg-white border-gray-200 hover:border-[#5A8BA6]/30'
                }`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Hover Glow */}
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
                  isDark ? 'from-[#7BA7C2]/10 to-transparent' : 'from-[#5A8BA6]/5 to-transparent'
                }`} />
                
                <div className="relative">
                  {/* Icon */}
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-500 ${
                    isDark 
                      ? 'bg-[#7BA7C2]/10 group-hover:bg-[#7BA7C2]/20' 
                      : 'bg-[#5A8BA6]/10 group-hover:bg-[#5A8BA6]/20'
                  }`}>
                    <IconComponent className={`w-7 h-7 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                  </div>

                  {/* Content */}
                  <h3 className={`text-xl font-semibold mb-3 transition-colors duration-300 ${
                    isDark 
                      ? 'text-white group-hover:text-[#7BA7C2]' 
                      : 'text-gray-900 group-hover:text-[#5A8BA6]'
                  }`}>
                    {translatedTitle}
                  </h3>
                  <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                    {translatedDesc}
                  </p>
                </div>

                {/* Decorative Line */}
                <div className={`absolute bottom-0 left-8 right-8 h-px bg-gradient-to-r from-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
                  isDark ? 'via-[#7BA7C2]/30' : 'via-[#5A8BA6]/30'
                }`} />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Expertise;
